/**
 * Utilitários para manipulação de URLs de fotos no frontend
 */
import { getStorageBaseUrl as getProductionStorageUrl } from '../config/production';

/**
 * Obtém a URL base do storage baseada no ambiente
 */
export function getStorageBaseUrl(): string {
  return getProductionStorageUrl();
}

/**
 * Constrói a URL completa de uma foto
 * @param filename - Nome do arquivo da foto
 * @param type - Tipo de URL (storage, thumbnail, image)
 * @returns URL completa da foto
 */
export function getPhotoUrl(filename: string, type: 'storage' | 'thumbnail' | 'image' = 'storage'): string {
  if (!filename) {
    return '';
  }
  
  const baseUrl = getStorageBaseUrl();
  
  switch (type) {
    case 'thumbnail':
      return `${baseUrl}/files/photos/${filename}`;
    case 'image':
      return `${baseUrl}/files/photos/${filename}`;
    case 'storage':
    default:
      return `${baseUrl}/files/photos/${filename}`;
  }
}

/**
 * Constrói múltiplas URLs para uma foto
 * @param filename - Nome do arquivo da foto
 * @returns Objeto com todas as URLs da foto
 */
export function getPhotoUrls(filename: string) {
  const baseUrl = getStorageBaseUrl();
  const photoPath = `/files/photos/${filename}`;
  
  return {
    storageUrl: `${baseUrl}${photoPath}`,
    thumbnailUrl: `${baseUrl}${photoPath}`,
    imageUrl: `${baseUrl}${photoPath}`,
  };
}

/**
 * Processa um objeto de foto adicionando as URLs corretas
 * @param photo - Objeto da foto
 * @returns Objeto da foto com URLs processadas
 */
export function processPhotoUrls(photo: any) {
  if (!photo || !photo.filename) {
    return photo;
  }
  
  const urls = getPhotoUrls(photo.filename);
  
  return {
    ...photo,
    ...urls,
  };
}

/**
 * Processa uma lista de fotos adicionando as URLs corretas
 * @param photos - Array de fotos
 * @returns Array de fotos com URLs processadas
 */
export function processPhotosUrls(photos: any[]) {
  if (!Array.isArray(photos)) {
    return photos;
  }
  
  return photos.map(processPhotoUrls);
}

/**
 * Verifica se uma URL de foto é válida
 * @param url - URL da foto
 * @returns true se a URL é válida
 */
export function isValidPhotoUrl(url: string): boolean {
  if (!url) return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Obtém uma URL de fallback para fotos
 * @returns URL de imagem placeholder
 */
export function getPhotoPlaceholder(): string {
  return '/placeholder-photo.jpg';
}
